import React from 'react'
import { useParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/features/auth/authStore'
import { ViewerSurface } from '@/features/view-tfl-uploaded/components'
import { AddCommentDialog } from '@/features/view-tfl-uploaded/components/AddCommentDialog'
import { CommentAvatarContainer } from '@/features/view-tfl-uploaded/components/CommentAvatar/CommentAvatarContainer'
import { SelectionBubble } from '@/features/view-tfl-uploaded/components/SelectionBubble'
import { useHtmlViewerController } from '@/features/view-tfl-uploaded/hooks'

type HTMLViewerProps = {
  documentId: string
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export const HTMLViewer: React.FC<HTMLViewerProps> = ({
  documentId,
  isLoading,
  error,
  refetch,
}) => {
  const { id } = useParams<{ id: string }>()
  const getListDLViewerHasAccess = useAuthStore(
    (state) => state.getListDLViewerHasAccess
  )
  const listDLViewerHasAccess = getListDLViewerHasAccess() as string[]
  const isViewer = listDLViewerHasAccess.includes(id as string)

  const {
    htmlById,
    containerRef,
    shadowHostRef,
    shadowRootRef,
    pendingBubble,
    dialogState,
    avatars,
    onAvatarClick,
    onClickOutside,
    bubbleOffset,
  } = useHtmlViewerController({ documentId })

  if (isLoading && !htmlById) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (error && !htmlById) {
    return (
      <div
        className="flex flex-col items-center justify-center p-8"
        role="alert"
      >
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-red-900">
            Failed to load file
          </h3>
          <p className="mb-4 text-sm text-red-700">
            {error.message || 'Please try again later.'}
          </p>
          <Button onClick={() => refetch()} variant="solid" color="primary">
            Reload
          </Button>
        </div>
      </div>
    )
  }

  if (!htmlById) {
    return (
      <div
        className="flex flex-col items-center justify-center space-y-4 p-8"
        role="alert"
        aria-live="assertive"
      >
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-red-900">
            Something went wrong
          </h3>
          <Button onClick={refetch} variant="solid" color="primary">
            Reload
          </Button>
        </div>
      </div>
    )
  }

  return (
    <ViewerSurface
      documentId={documentId}
      scrollContainerRef={containerRef}
      isLoading={isLoading && !!htmlById}
      scrollClassName="relative"
      onClick={onClickOutside}
    >
      {!isViewer && pendingBubble.show && pendingBubble.position && (
        <SelectionBubble
          position={pendingBubble.position}
          onAddComment={pendingBubble.onAddComment}
        />
      )}

      {dialogState.documentVersionId && (
        <AddCommentDialog
          open={dialogState.open}
          onOpenChange={dialogState.onOpenChange}
          documentVersionId={dialogState.documentVersionId}
          annotation={dialogState.annotation}
          onSuccess={dialogState.onSuccess}
        />
      )}

      {avatars.map((avatar) => {
        if (!shadowRootRef.current) return null
        return (
          <CommentAvatarContainer
            key={avatar.commentId}
            comment={avatar.comment}
            fragment={avatar.fragment}
            containerRef={containerRef}
            shadowRootRef={shadowRootRef as React.RefObject<ShadowRoot>}
            engine={avatar.engine}
            isActive={avatar.isActive}
            onClick={onAvatarClick}
            offset={bubbleOffset}
          />
        )
      })}

      <div
        ref={shadowHostRef}
        className="block w-full p-6"
        aria-label="TFL document viewer"
      />
    </ViewerSurface>
  )
}
